import React, { useEffect, useState } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Eye, ClipboardList, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { DateInput } from "../components/ui/dateInput";
import Pagination from "../components/ui/pagination";
import { useSearchParams } from "react-router-dom";

interface AuditLog {
	id: number;
	entity_id: number;
	user_id: string;
	action: string;
	table_name: string;
	record_id: number;
	old_data: any;
	new_data: any;
	created_at: string;
}

const AuditLogs: React.FC = () => {
	const [logs, setLogs] = useState<AuditLog[]>([]);
	const [loading, setLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
	const { toast } = useToast();
	const [searchParams, setSearchParams] = useSearchParams();

	const [totalCount, setTotalCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
	const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 25);

	const [filters, setFilters] = useState({
		entity_id: searchParams.get("entity_id") || "",
		table_name: searchParams.get("table_name") || "all",
		action: searchParams.get("action") || "all",
		start_date: searchParams.get("start_date") || new Date().toISOString().split("T")[0],
		end_date: searchParams.get("end_date") || new Date().toISOString().split("T")[0],
	});

	useEffect(() => {
		const params: any = {};
		if (filters.entity_id) params.entity_id = filters.entity_id;
		if (filters.table_name !== "all") params.table_name = filters.table_name;
		if (filters.action !== "all") params.action = filters.action;
		if (filters.start_date) params.start_date = filters.start_date;
		if (filters.end_date) params.end_date = filters.end_date;
		params.page = currentPage.toString();
		params.limit = limit.toString();
		setSearchParams(params);

		fetchLogs();
	}, [filters, currentPage, limit]);

	const fetchLogs = async () => {
		setLoading(true);
		try {
			const queryParams: any = {
				limit: limit,
				offset: (currentPage - 1) * limit,
				start_date: filters.start_date,
				end_date: filters.end_date,
			};
			if (filters.entity_id) queryParams.entity_id = Number(filters.entity_id);
			if (filters.table_name !== "all") queryParams.table_name = filters.table_name;
			if (filters.action !== "all") queryParams.action = filters.action;

			const response = await api.getAuditLogs(queryParams);
			if (response?.success) {
				setLogs(response.data || []);
				setTotalCount(response.totalCount || 0);
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response?.error || "Failed to fetch audit logs",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to fetch audit logs",
			});
		} finally {
			setLoading(false);
		}
	};

	const resetFilters = () => {
		setFilters({
			entity_id: "",
			table_name: "all",
			action: "all",
			start_date: new Date().toISOString().split("T")[0],
			end_date: new Date().toISOString().split("T")[0],
		});
		setCurrentPage(1);
	};

	const handleOpenViewer = (log: AuditLog) => {
		setSelectedLog(log);
		setIsModalOpen(true);
	};

	const formatActionBadge = (action: string) => {
		switch (action) {
			case "CREATE":
				return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">CREATE</Badge>;
			case "UPDATE":
				return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">UPDATE</Badge>;
			case "DELETE":
				return <Badge className="bg-red-100 text-red-800 hover:bg-red-200">DELETE</Badge>;
			default:
				return <Badge variant="secondary">{action}</Badge>;
		}
	};

	return (
		<div className="p-6 min-h-screen bg-white dark:bg-gray-900">
			<div className="flex justify-between items-center mb-6">
				<div>
					<h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
						<ClipboardList className="w-6 h-6" />
						Audit Logs
					</h1>
					<p className="text-gray-600 dark:text-gray-400">
						Review historical changes to entities, pincodes, and allocation rules
					</p>
				</div>
			</div>

			<div className="flex flex-wrap gap-4 mb-6 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg items-end">
				<div className="flex-1 min-w-[150px]">
					<Label className="mb-2 block">Entity ID</Label>
					<Input
						placeholder="Filter by Entity ID"
						value={filters.entity_id}
						onChange={(e) => {
							setFilters({ ...filters, entity_id: e.target.value });
							setCurrentPage(1);
						}}
					/>
				</div>
				<div className="flex-1 min-w-[150px]">
					<Label className="mb-2 block">Table</Label>
					<Select
						value={filters.table_name}
						onValueChange={(val) => {
							setFilters({ ...filters, table_name: val });
							setCurrentPage(1);
						}}
					>
						<SelectTrigger>
							<SelectValue placeholder="All Tables" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Tables</SelectItem>
							<SelectItem value="entities">entities</SelectItem>
							<SelectItem value="entities_pincodes">entities_pincodes</SelectItem>
							<SelectItem value="entity_rules">entity_rules</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="flex-1 min-w-[150px]">
					<Label className="mb-2 block">Action</Label>
					<Select
						value={filters.action}
						onValueChange={(val) => {
							setFilters({ ...filters, action: val });
							setCurrentPage(1);
						}}
					>
						<SelectTrigger>
							<SelectValue placeholder="All Actions" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Actions</SelectItem>
							<SelectItem value="CREATE">CREATE</SelectItem>
							<SelectItem value="UPDATE">UPDATE</SelectItem>
							<SelectItem value="DELETE">DELETE</SelectItem>
							<SelectItem value="BULK_ADD_PINCODES">BULK ADD PINCODES</SelectItem>
							<SelectItem value="BULK_REMOVE_PINCODES">BULK REMOVE PINCODES</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center space-x-2">
					<div className="flex flex-col">
						<Label className="mb-2 block">Date Range</Label>
						<div className="flex items-center space-x-2">
							<DateInput
								value={filters.start_date}
								onChange={(date) => {
									setFilters({ ...filters, start_date: date });
									setCurrentPage(1);
								}}
							/>
							<span className="dark:text-white">-</span>
							<DateInput
								value={filters.end_date}
								onChange={(date) => {
									setFilters({ ...filters, end_date: date });
									setCurrentPage(1);
								}}
							/>
						</div>
					</div>
				</div>
				<div className="flex space-x-2">
					<Button variant="outline" onClick={resetFilters} title="Reset Filters">
						<RotateCcw className="w-4 h-4" />
					</Button>
					<Button onClick={fetchLogs} disabled={loading}>
						{loading ? "Loading..." : "Refresh"}
					</Button>
				</div>
			</div>

			<div className="flex justify-between items-center mb-4">
				<div className="flex items-center space-x-2 dark:text-white">
					<span className="text-sm text-gray-500">Show: </span>
					<Select
						value={limit.toString()}
						onValueChange={(value) => {
							setLimit(Number(value));
							setCurrentPage(1);
						}}
					>
						<SelectTrigger className="min-w-[80px]">
							<SelectValue placeholder="Select limit" />
						</SelectTrigger>
						<SelectContent className="min-w-[80px]">
							<SelectItem value="10">10</SelectItem>
							<SelectItem value="25">25</SelectItem>
							<SelectItem value="50">50</SelectItem>
							<SelectItem value="100">100</SelectItem>
						</SelectContent>
					</Select>
					<span className="text-sm text-gray-500 ml-2">Total records: {totalCount}</span>
				</div>

				<Pagination
					page={currentPage}
					totalPages={Math.ceil(totalCount / limit)}
					onPageChange={(page) => setCurrentPage(page)}
				/>
			</div>

			<div className="border rounded-md overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>ID</TableHead>
							<TableHead>Date</TableHead>
							<TableHead>User</TableHead>
							<TableHead>Action</TableHead>
							<TableHead>Table</TableHead>
							<TableHead>Record ID</TableHead>
							<TableHead>Entity ID</TableHead>
							<TableHead>Details</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{logs.length === 0 ? (
							<TableRow>
								<TableCell colSpan={8} className="text-center py-8 text-gray-500">
									No audit logs match your filters
								</TableCell>
							</TableRow>
						) : (
							logs.map((log) => (
								<TableRow key={log.id}>
									<TableCell>{log.id}</TableCell>
									<TableCell>
										{new Date(log.created_at).toLocaleString()}
									</TableCell>
									<TableCell className="font-mono text-xs">{log.user_id}</TableCell>
									<TableCell>{formatActionBadge(log.action)}</TableCell>
									<TableCell className="font-medium">{log.table_name}</TableCell>
									<TableCell>{log.record_id || "-"}</TableCell>
									<TableCell>{log.entity_id || "-"}</TableCell>
									<TableCell>
										<Button
											variant="outline"
											size="sm"
											onClick={() => handleOpenViewer(log)}
										>
											<Eye className="w-4 h-4 mr-1" /> View
										</Button>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>

			<Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
				<DialogContent className="sm:max-w-[800px] max-h-[85vh] overflow-hidden flex flex-col">
					<DialogHeader>
						<DialogTitle>Audit Log Details #{selectedLog?.id}</DialogTitle>
					</DialogHeader>

					{selectedLog && (
						<div className="grid grid-cols-1 gap-4 py-4 overflow-y-auto">
							<div className="flex flex-wrap gap-4 text-sm mb-4 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border">
								<div><strong>Action:</strong> {formatActionBadge(selectedLog.action)}</div>
								<div><strong>Table:</strong> <Badge variant="outline">{selectedLog.table_name}</Badge></div>
								<div><strong>Record ID:</strong> {selectedLog.record_id || "-"}</div>
								<div><strong>User:</strong> {selectedLog.user_id}</div>
							</div>

							<div className="flex flex-col">
								<h3 className="text-sm font-medium mb-3 text-gray-500 border-b pb-2">Changes</h3>
								<div className="flex flex-col gap-3 font-mono">
									{(!selectedLog.old_data && !selectedLog.new_data) ? (
										<div className="text-center text-gray-500 py-4">No data payload recorded</div>
									) : (
										Array.from(new Set([...Object.keys(selectedLog.old_data || {}), ...Object.keys(selectedLog.new_data || {})])).map(key => {
											const oldVal = selectedLog.old_data?.[key];
											const newVal = selectedLog.new_data?.[key];
											const isAdded = oldVal === undefined && newVal !== undefined;
											const isRemoved = oldVal !== undefined && newVal === undefined;
											const isChanged = oldVal !== undefined && newVal !== undefined && JSON.stringify(oldVal) !== JSON.stringify(newVal);

											if (!isAdded && !isRemoved && !isChanged && key !== 'pincodes') return null;

											const renderValue = (val: any) => {
												if (Array.isArray(val)) {
													if (val.length === 0) return <span className="italic text-gray-400">Empty array</span>;
													return (
														<div className="flex flex-wrap gap-1 mt-1 max-h-40 overflow-y-auto p-1">
															{val.map((v, i) => (
																<Badge key={i} variant="secondary" className="font-mono text-xs">{String(v)}</Badge>
															))}
														</div>
													);
												}
												if (typeof val === 'object' && val !== null) {
													return <pre className="mt-1 bg-white dark:bg-gray-950 p-2 text-xs rounded border">{JSON.stringify(val, null, 2)}</pre>;
												}
												return <span className="ml-1 break-all">{String(val)}</span>;
											};

											return (
												<div key={key} className="p-3 border rounded-md bg-gray-50 dark:bg-gray-900/50 text-sm">
													<div className="font-semibold text-gray-700 dark:text-gray-300 mb-2">{key.replace(/_/g, ' ')}</div>
													{isAdded && (
														<div className="text-green-700 dark:text-green-400 flex items-start">
															<span className="font-bold mr-2 mt-0.5">+</span>
															<div className="flex-1">{renderValue(newVal)}</div>
														</div>
													)}
													{isRemoved && (
														<div className="text-red-700 dark:text-red-400 flex items-start">
															<span className="font-bold mr-2 mt-0.5">-</span>
															<div className="flex-1">{renderValue(oldVal)}</div>
														</div>
													)}
													{isChanged && (
														<div className="flex flex-col gap-2">
															<div className="text-red-700 dark:text-red-400 flex items-start bg-red-50 dark:bg-red-950/30 p-2 rounded">
																<span className="font-bold mr-2 mt-0.5">-</span>
																<div className="flex-1">{renderValue(oldVal)}</div>
															</div>
															<div className="text-green-700 dark:text-green-400 flex items-start bg-green-50 dark:bg-green-950/30 p-2 rounded">
																<span className="font-bold mr-2 mt-0.5">+</span>
																<div className="flex-1">{renderValue(newVal)}</div>
															</div>
														</div>
													)}
												</div>
											);
										})
									)}
								</div>
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default AuditLogs;
